import { useState } from "react"

import {
  AUMFeeRuleField,
  AUMFeeRuleFields,
  AUMFeeRuleOperator,
  AUMFeeRuleOperators,
  ConnectorAUMFee,
  ConnectorAUMFeeRuleSet,
} from "wealthmode-frontend/backend"
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Heading,
  Input,
  InputField,
  Select,
  SelectBackdrop,
  SelectContent,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"
import { ChevronDownIcon } from "wealthmode-frontend/icons"

import { PercentField } from "@/components/controls"
import { percentToRate, rateToPercent } from "@/lib/rate"

const FIELD_LABELS: Record<AUMFeeRuleField, string> = {
  name: "Name",
  type: "Type",
  subtype: "Subtype",
  variant: "Variant",
  kind: "Kind",
}

const OPERATOR_LABELS: Record<AUMFeeRuleOperator, string> = {
  contains: "contains",
  not_contains: "does not contain",
  equals: "equals",
  not_equals: "does not equal",
  starts_with: "starts with",
  ends_with: "ends with",
}

// Drafts carry keys of their own because rule sets are reordered, and index
// keys would hand each row's input state to its neighbour.
type DraftRule = {
  key: number
  field: AUMFeeRuleField
  operator: AUMFeeRuleOperator
  value: string
}

type DraftRuleSet = {
  key: number
  percent: string
  rules: DraftRule[]
}

let nextKey = 0

/**
 * AUMFeeEditor curates a connector's official AUM fee: a rate charged on every
 * account the connector serves, and rule sets that assign a different rate to
 * the accounts they match. Rule sets are tried top to bottom, every rule in a
 * set must match, and the first set to match decides.
 */
export function AUMFeeEditor({ aumFee }: { aumFee: ConnectorAUMFee }) {
  const [percent, setPercent] = useState(() => rateToPercent(aumFee.attributes.rate))
  const [ruleSets, setRuleSets] = useState(() => toDrafts(aumFee.attributes.rule_sets))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function updateRuleSet(key: number, change: (ruleSet: DraftRuleSet) => DraftRuleSet) {
    setRuleSets((current) => current.map((ruleSet) => (ruleSet.key === key ? change(ruleSet) : ruleSet)))
  }

  function updateRule(ruleSetKey: number, ruleKey: number, change: Partial<Omit<DraftRule, "key">>) {
    updateRuleSet(ruleSetKey, (ruleSet) => ({
      ...ruleSet,
      rules: ruleSet.rules.map((rule) => (rule.key === ruleKey ? { ...rule, ...change } : rule)),
    }))
  }

  function move(index: number, offset: -1 | 1) {
    setRuleSets((current) => {
      const target = index + offset
      if (target < 0 || target >= current.length) {
        return current
      }

      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  async function save() {
    setSaving(true)
    setError(null)

    try {
      await aumFee.saveCuration(percentToRate(percent), fromDrafts(ruleSets))

      setPercent(rateToPercent(aumFee.attributes.rate))
      setRuleSets(toDrafts(aumFee.attributes.rule_sets))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <VStack className="gap-12">
      <VStack>
        <Heading size="md">AUM fee</Heading>
        <Text className="text-xs text-foreground-secondary">
          The connector rate applies to every account unless a rule set matches it. Rule sets are tried top to bottom,
          every rule in a set must match, and the first match decides. Blank means unknown; enter 0 for accounts known
          to pay nothing. Accounts pick up changes when their connection next syncs.
        </Text>
      </VStack>

      <PercentField
        label="Connector rate"
        value={percent}
        onChange={setPercent}
      />

      <VStack className="gap-6">
        {ruleSets.map((ruleSet, index) => (
          <VStack
            key={ruleSet.key}
            className="gap-4 rounded-sm border border-border-medium px-6 py-6"
          >
            <HStack className="items-center justify-between gap-4">
              <Text className="text-sm text-foreground-primary">Rule set {index + 1}</Text>
              <HStack className="items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={index === 0}
                  onPress={() => move(index, -1)}
                >
                  <ButtonText>Up</ButtonText>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={index === ruleSets.length - 1}
                  onPress={() => move(index, 1)}
                >
                  <ButtonText>Down</ButtonText>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onPress={() => setRuleSets((current) => current.filter((other) => other.key !== ruleSet.key))}
                >
                  <ButtonText>Remove</ButtonText>
                </Button>
              </HStack>
            </HStack>

            <PercentField
              label="Rate"
              value={ruleSet.percent}
              onChange={(value) => updateRuleSet(ruleSet.key, (current) => ({ ...current, percent: value }))}
            />

            {ruleSet.rules.map((rule) => (
              <HStack
                key={rule.key}
                className="items-center gap-2"
              >
                <Choice
                  value={rule.field}
                  options={AUMFeeRuleFields}
                  labels={FIELD_LABELS}
                  onChange={(field) => updateRule(ruleSet.key, rule.key, { field })}
                />
                <Choice
                  value={rule.operator}
                  options={AUMFeeRuleOperators}
                  labels={OPERATOR_LABELS}
                  onChange={(operator) => updateRule(ruleSet.key, rule.key, { operator })}
                />
                <Input
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <InputField
                    value={rule.value}
                    onChangeText={(value: string) => updateRule(ruleSet.key, rule.key, { value })}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </Input>
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={ruleSet.rules.length === 1}
                  onPress={() =>
                    updateRuleSet(ruleSet.key, (current) => ({
                      ...current,
                      rules: current.rules.filter((other) => other.key !== rule.key),
                    }))
                  }
                >
                  <ButtonText>Remove</ButtonText>
                </Button>
              </HStack>
            ))}

            <HStack>
              <Button
                size="sm"
                variant="outline"
                onPress={() =>
                  updateRuleSet(ruleSet.key, (current) => ({ ...current, rules: [...current.rules, newRule()] }))
                }
              >
                <ButtonText>Add rule</ButtonText>
              </Button>
            </HStack>
          </VStack>
        ))}

        <HStack>
          <Button
            size="sm"
            variant="outline"
            onPress={() => setRuleSets((current) => [...current, { key: nextKey++, percent: "", rules: [newRule()] }])}
          >
            <ButtonText>Add rule set</ButtonText>
          </Button>
        </HStack>
      </VStack>

      {error ? (
        <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
          <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
        </Box>
      ) : null}

      <HStack>
        <Button
          action="primary"
          className="bg-background-contrast"
          isDisabled={saving}
          onPress={() => void save()}
        >
          <ButtonText className="text-foreground-contrast">{saving ? "Saving…" : "Save AUM fee"}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  )
}

function Choice<T extends string>({
  value,
  options,
  labels,
  onChange,
}: {
  value: T
  options: readonly T[]
  labels: Record<T, string>
  onChange: (value: T) => void
}) {
  return (
    <Select
      selectedValue={value}
      selectedLabel={labels[value]}
      onValueChange={(next: string) => onChange(next as T)}
    >
      <SelectTrigger
        variant="outline"
        size="sm"
        // The stock trigger heights predate this project's spacing scale; h-20
        // is what a small Input renders at, so the row lines up.
        className="h-20 w-44"
      >
        <SelectInput className="flex-1" />
        <SelectIcon
          as={ChevronDownIcon}
          className="mr-2"
        />
      </SelectTrigger>
      <SelectPortal>
        <SelectBackdrop />
        <SelectContent className="bg-transparent">
          {options.map((option) => (
            <SelectItem
              key={option}
              label={labels[option]}
              value={option}
            />
          ))}
        </SelectContent>
      </SelectPortal>
    </Select>
  )
}

function newRule(): DraftRule {
  return { key: nextKey++, field: "name", operator: "contains", value: "" }
}

function toDrafts(ruleSets: ConnectorAUMFeeRuleSet[]): DraftRuleSet[] {
  return ruleSets.map((ruleSet) => ({
    key: nextKey++,
    percent: rateToPercent(ruleSet.rate),
    rules: ruleSet.rules.map((rule) => ({ ...rule, key: nextKey++ })),
  }))
}

function fromDrafts(drafts: DraftRuleSet[]): ConnectorAUMFeeRuleSet[] {
  return drafts.map((draft, index) => {
    const rate = percentToRate(draft.percent)
    if (rate === null) {
      throw new Error(`Rule set ${index + 1} needs a rate`)
    }

    const rules = draft.rules.map(({ field, operator, value }) => ({ field, operator, value: value.trim() }))
    if (rules.some((rule) => rule.value === "")) {
      throw new Error(`Rule set ${index + 1} has a rule with no value`)
    }

    return { rate, rules }
  })
}
